import { FunctionComponent, useEffect, useState } from 'react'
import { Daycare, DaycareType } from '@/types/Daycare'
import { useMutation } from '@apollo/client'
import styles from './parentSuggest.module.scss'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/modules/context/AuthContext'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptionsDeprecated } from '@/types/Select'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import UserInput from '@/components/_custom/UserInput/userInput'
import { SET_PARENT_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface StaffSuggestProps {
    daycare: Daycare
    parentDistance: string
    setParentDistance: (value: string) => void
    parentMinMonth: number
    setParentMinMonth: (value: number) => void
    parentMaxMonth: number
    setParentMaxMonth: (value: number) => void
    canUpdate: boolean
}

const ParentSuggest: FunctionComponent<StaffSuggestProps> = ({
    daycare,
    parentDistance,
    setParentDistance,
    parentMinMonth,
    setParentMinMonth,
    parentMaxMonth,
    setParentMaxMonth,
    canUpdate
}: StaffSuggestProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newParentDistance, setNewParentDistance] = useState<string>(parentDistance)
    const [newParentMinMonth, setNewParentMinMonth] = useState<string>(parentMinMonth.toString())
    const [newParentMaxMonth, setNewParentMaxMonth] = useState<string>(parentMaxMonth.toString())

    const [showAgeInMonth, setShowAgeInMonth] = useState<boolean>(
        daycare.type === DaycareType.daycare ||
            daycare.type === DaycareType.nursery ||
            daycare.type === DaycareType.multiHome ||
            daycare.type === DaycareType.playCenter
    )

    useEffect(() => {
        const canShowAgeInMonth: boolean =
            daycare.type === DaycareType.daycare ||
            daycare.type === DaycareType.nursery ||
            daycare.type === DaycareType.multiHome ||
            daycare.type === DaycareType.playCenter

        setShowAgeInMonth(canShowAgeInMonth)

        setNewParentDistance(parentDistance)
        setNewParentMinMonth(canShowAgeInMonth ? parentMinMonth.toString() : (parentMinMonth / 12).toString())
        setNewParentMaxMonth(canShowAgeInMonth ? parentMaxMonth.toString() : (parentMaxMonth / 12).toString())
    }, [daycare, parentDistance, parentMinMonth, parentMaxMonth])
    useEffect(() => {}, [])

    const [MIN_AGE_IN_MONTH, MAX_AGE_IN_MONTH] = getAgeInMonthByDaycareType(daycare.type)

    const parentDistanceOptions: SelectOptionsDeprecated = [
        {
            options: [
                {
                    name: '0 km - ' + daycare.city + ' ' + t('only').toLowerCase(),
                    value: 'distance_0'
                },
                {
                    name: ' 5 km',
                    value: 'distance_5'
                },
                {
                    name: '10 km',
                    value: 'distance_10'
                },
                {
                    name: '20 km',
                    value: 'distance_20'
                }
            ]
        }
    ]

    const [setParentSuggestSettings] = useMutation(SET_PARENT_SUGGEST_SETTINGS, {
        variables: {
            daycareId: daycare.id,
            distance: newParentDistance,
            maxMonth: showAgeInMonth ? Number(newParentMaxMonth) : Math.floor(Number(newParentMaxMonth) * 12),
            minMonth: showAgeInMonth ? Number(newParentMinMonth) : Math.floor(Number(newParentMinMonth) * 12)
        },
        onCompleted: () => {
            fetchCurrentDaycare()

            setParentDistance(newParentDistance)
            setParentMinMonth(showAgeInMonth ? Number(newParentMinMonth) : Math.floor(Number(newParentMinMonth) * 12))
            setParentMaxMonth(showAgeInMonth ? Number(newParentMaxMonth) : Math.floor(Number(newParentMaxMonth) * 12))

            showNotificationSuccess(t('suggestSettingsUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdatingSuggestSettings'))
        }
    })

    const disabled: boolean =
        newParentDistance === parentDistance &&
        newParentMinMonth === (showAgeInMonth ? parentMinMonth.toString() : (parentMinMonth / 12).toString()) &&
        newParentMaxMonth === (showAgeInMonth ? parentMaxMonth.toString() : (parentMaxMonth / 12).toString())

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    return (
        <div className={styles.ParentSuggestPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={isRpeOrLaep ? t('publicSettings') : t('familySettings')} className={styles.Title} />
                </div>
                <Table className={styles.SettingsTable}>
                    <TableBody>
                        <TableRow className={styles.TableRow}>
                            <TableCell className={styles.RowText}>{t('inRadiusOf')}</TableCell>
                            <TableCell>
                                <SelectUnique
                                    selectOptions={parentDistanceOptions}
                                    selectValue={newParentDistance}
                                    setSelectValue={setNewParentDistance}
                                    formClassName={styles.UserInput}
                                    readOnly={!canUpdate}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow className={styles.TableRow}>
                            <TableCell className={`${styles.RowText} ${styles.HideBorder}`}>
                                {t('ageMinimum')}
                            </TableCell>
                            <TableCell className={styles.HideBorder}>
                                <UserInput
                                    type={'number'}
                                    value={newParentMinMonth}
                                    setValue={setNewParentMinMonth}
                                    endAdornment={showAgeInMonth ? t('months') : t('yearsOld')}
                                    className={styles.UserInput}
                                    verify={'number'}
                                    minNumber={MIN_AGE_IN_MONTH}
                                    maxNumber={Number(newParentMaxMonth) - 1}
                                    disabled={!canUpdate}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow className={styles.TableRow}>
                            <TableCell className={styles.RowText}>{t('ageMaximum')}</TableCell>
                            <TableCell className={styles.RowText}>
                                <UserInput
                                    type={'number'}
                                    value={newParentMaxMonth}
                                    setValue={setNewParentMaxMonth}
                                    endAdornment={showAgeInMonth ? t('months') : t('yearsOld')}
                                    className={styles.UserInput}
                                    verify={'number'}
                                    minNumber={Number(newParentMinMonth) + 1}
                                    maxNumber={MAX_AGE_IN_MONTH}
                                    disabled={!canUpdate}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                {canUpdate && (
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'update'}
                            onClick={setParentSuggestSettings}
                            disabled={disabled}
                            className={styles.Button}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default ParentSuggest

function getAgeInMonthByDaycareType(daycareType: DaycareType): [number, number] {
    switch (daycareType) {
        case DaycareType.daycare:
        case DaycareType.multiHome:
        case DaycareType.playCenter:
        case DaycareType.nursery:
            return [0, 48] // 0-48 months
        case DaycareType.microDaycare:
        case DaycareType.laep:
        case DaycareType.rpe:
            return [0, 6] // 0-6 years
        case DaycareType.recreationCenter:
            return [2, 18] // 2-18 years
        case DaycareType.toyLibrary:
            return [0, 18] // 0-18 years
        default:
            return [0, 18] // 0-18 years
    }
}
